/**
 * Nextcloud Extension - Phase 1a Prototype
 * Mock data only, no backend routes. Shows: Settings connect panel (mocked),
 * Browse tree in sidebar, file preview/open.
 *
 * Access: http://app.maater.localhost/?test=nextcloud-extension
 *
 * Follows: Drive patterns (SidebarTreeRow, preview pane)
 * SoT: /?test=maater-ui for icons/menus/theme
 * Bet: docs/product/bets/2026-09-13-nextcloud-extension.md
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Folder,
  FolderOpen,
  FileText,
  File,
  Image as ImageIcon,
  FileCode,
  Lock,
  Download,
  Import,
  Link2,
  Check,
  X,
  Globe,
  Loader2,
} from 'lucide-react'
import { Panel, PanelGroup } from 'react-resizable-panels'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { testIds } from '@/lib/test-ids'
import { wsNavRowPadLeftPx } from '@/components/layout/left-sidebar.components'
import { SidebarTreeRow } from '@/components/layout/sidebar/SidebarTreeRow'
import { getDocumentDisplayIcon } from '@/lib/document-icons'
import { type BreadcrumbSegment } from '@/components/ui/address-bar'
import { ListSurfaceStatusBar } from '@/components/ui/list-surface-status-bar'
import { SecondaryPreviewResizeHandle } from '@/components/integrations/_shared/secondary-preview-resize-handle'
import {
  FolderFilterRow,
  FolderItemCard,
  FolderTitleRow,
  type FolderKindFilter,
  type FolderViewMode,
} from '@/components/integrations/_shared/folder-view-chrome'
import { FOLDER_CARD_GRID_CLASS } from '@/components/features/document-list-items'
import { DocumentTileMarkdown } from '@/components/features/document-tile-markdown'
import { SecondaryPanel } from '@/components/layout/secondary-panel'
import { PreviewPaneHeader } from '@/components/features/preview-pane-header'
import { ExtensionListRow } from '@/components/integrations/_shared/extension-list-row'
import { IndexSurfaceShell } from '@/components/integrations/_shared'
import { IndexPreviewEmpty } from '@/components/integrations/_shared/index-preview-empty'
import { UpOneLevelRow } from '@/components/ui/up-one-level-row'
import { LIST_GESTURE_TIPS_DRIVE_WITH_PREVIEW } from '@/lib/list-gesture-tips'
import { resolveOpenExternalIcon } from '@/lib/open-destination-icons'
import {
  EntityContextMenu,
  type EntityContextMenuEntry,
} from '@/components/features/entity-context-menu'
import { PANEL_SIZES } from '@/lib/panel-sizes'
import { wsNavSectionStyle } from '@/lib/ws-nav'
import {
  maaterProtoAppSkinStyle,
  MaaterProtoSegmentTrack,
} from '@/components/prototype/maater-proto-shell'
import {
  ProtoAppHeader,
  ProtoIconRail,
  ProtoModePanel,
  ProtoTabRow,
} from '@/components/prototype/maater-proto-chrome'
import { useTheme } from '@/providers/theme-provider'
import { OpenDestinationIcons } from '@/lib/open-destination-icons'
import {
  MOCK_NEXTCLOUD_ROOT,
  MOCK_NEXTCLOUD_SHARED,
  MOCK_NEXTCLOUD_SHARES_OUT,
  isNextcloudFileImportable,
  type MockNextcloudFile,
  type NextcloudFileKind,
} from '@/components/integrations/nextcloud/mock-nextcloud-tree'

// --- Types ----------------------------------------------------------------

type AuthState = 'disconnected' | 'connecting' | 'connected'
type BrowseSection = 'allFiles' | 'sharedIn' | 'sharesOut'

const BROWSE_SURFACES: { id: BrowseSection; label: string }[] = [
  { id: 'allFiles', label: 'Files' },
  { id: 'sharedIn', label: 'Shared with me' },
  { id: 'sharesOut', label: 'Shares you created' },
]

const INITIAL_FILE = findFileById(MOCK_NEXTCLOUD_ROOT, 'project-notes')
const START_PATH = defaultRootPath('/Maater')

type CtxMenu = {
  x: number
  y: number
  file: MockNextcloudFile
}

type NcFrame = {
  section: BrowseSection
  path: string[]
  view: 'folder' | 'file'
  fileId: string | null
}

// --- Mock content ---------------------------------------------------------

const MOCK_FILE_CONTENT: Record<string, string> = {
  'project-notes.md': `# Project Notes

## Overview
Knowledge management infrastructure for the Lightweb community.

## Key Points
- Nextcloud as the file store
- Maater for searchable memory
- Sync philosophy: explicit pull, opt-in Keep synced`,
  'sovereign-tech.md': `# Sovereign Technology

Notes on self-hosted infrastructure and digital sovereignty.

- Community-owned services
- Data locality
- Zero-trust architecture`,
  'lightweb-notes.md': `# Lightweb Integration Notes

Planning doc for Nextcloud + Maater integration at Lightweb.

## Decision Points
1. On-prem licensing
2. Embedding locality
3. Lane split: NC = files, Maater = memory`,
  'meeting-agenda.txt': `Meeting Agenda - October 2026

1. Review sprint progress
2. Discuss Nextcloud integration
3. Plan next phase
4. Action items`,
  'collaboration-doc.md': `# Collaboration Document

Shared notes from the team.

This is a shared document from another user's Nextcloud.`,
  'technical-notes.md': `# Technical Notes

Shared with external collaborators via Nextcloud share link.

## Topics
- Architecture decisions
- Implementation approach`,
}

// --- Component ------------------------------------------------------------

export default function TestNextcloudExtension() {
  const { theme, setTheme } = useTheme()
  const [authState, setAuthState] = useState<AuthState>('connected')
  const [connecting, setConnecting] = useState(false)
  const [instanceUrl, setInstanceUrl] = useState('https://cloud.example.org')
  const [username, setUsername] = useState('alice')
  const [defaultRoot, setDefaultRoot] = useState('/Maater')
  const [browseSection, setBrowseSection] = useState<BrowseSection>('allFiles')
  const [currentFolderPath, setCurrentFolderPath] = useState<string[]>(START_PATH)
  const [expandedIds, setExpandedIds] = useState<Set<string>>(
    () => new Set(['allFiles', ...START_PATH]),
  )
  const [selectedId, setSelectedId] = useState<string | null>(INITIAL_FILE?.id ?? null)
  const [selectedFile, setSelectedFile] = useState<MockNextcloudFile | null>(INITIAL_FILE)
  const [previewOpen, setPreviewOpen] = useState(true)
  const [previewFile, setPreviewFile] = useState<MockNextcloudFile | null>(INITIAL_FILE)
  const [mainView, setMainView] = useState<'folder' | 'file'>('folder')
  const [ctxMenu, setCtxMenu] = useState<CtxMenu | null>(null)
  const [importing, setImporting] = useState<string | null>(null)
  const [toast, setToast] = useState<string | null>(null)
  const [importModalOpen, setImportModalOpen] = useState(false)
  const [importingFile, setImportingFile] = useState<MockNextcloudFile | null>(null)
  const [importDestination, setImportDestination] = useState('workspace/inbox/nextcloud')
  const [shareLinkToken, setShareLinkToken] = useState('')
  const [shareImportOpen, setShareImportOpen] = useState(false)
  const [testingConnection, setTestingConnection] = useState(false)
  const [connectionTestResult, setConnectionTestResult] = useState<'success' | 'error' | null>(null)
  const [errorState, setErrorState] = useState<'404' | '403' | null>(null)
  const [configureOpen, setConfigureOpen] = useState(false)
  const [viewMode, setViewMode] = useState<FolderViewMode>('list')
  const [kindFilter, setKindFilter] = useState<FolderKindFilter>('all')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')
  const frameRef = useRef<NcFrame>({
    section: 'allFiles',
    path: START_PATH,
    view: 'folder',
    fileId: INITIAL_FILE?.id ?? null,
  })
  const backRef = useRef<NcFrame[]>([])
  const forwardRef = useRef<NcFrame[]>([])
  const [historyTick, setHistoryTick] = useState(0)

  const handleConnect = useCallback(() => {
    setConnecting(true)
    setTimeout(() => {
      setAuthState('connected')
      setConnecting(false)
      setConfigureOpen(false)
    }, 1500)
  }, [])

  const handleDisconnect = useCallback(() => {
    setAuthState('disconnected')
  }, [])

  const toggleExpanded = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }, [])

  const handlePreviewToggle = useCallback(() => {
    setPreviewOpen(prev => !prev)
  }, [])

  useEffect(() => {
    frameRef.current = {
      section: browseSection,
      path: currentFolderPath,
      view: mainView,
      fileId: selectedId,
    }
  }, [browseSection, currentFolderPath, mainView, selectedId])

  const pushHistory = useCallback(() => {
    const snap = frameRef.current
    backRef.current = [...backRef.current, { ...snap, path: [...snap.path] }]
    forwardRef.current = []
    setHistoryTick((tick) => tick + 1)
  }, [])

  const handleContextMenu = useCallback(
    (e: React.MouseEvent, file: MockNextcloudFile) => {
      e.preventDefault()
      setCtxMenu({ x: e.clientX, y: e.clientY, file })
    },
    [],
  )

  const handleImport = useCallback((file: MockNextcloudFile) => {
    setImportingFile(file)
    setImportModalOpen(true)
    setCtxMenu(null)
  }, [])

  const confirmImport = useCallback(() => {
    if (!importingFile) return
    setImporting(importingFile.id)
    setImportModalOpen(false)
    setTimeout(() => {
      setImporting(null)
      setToast(`Imported ${importingFile.name} to ${importDestination}`)
      setTimeout(() => setToast(null), 3000)
      setImportingFile(null)
    }, 1000)
  }, [importingFile, importDestination])

  const handleTestConnection = useCallback(() => {
    setTestingConnection(true)
    setTimeout(() => {
      setTestingConnection(false)
      setConnectionTestResult('success')
      setTimeout(() => setConnectionTestResult(null), 3000)
    }, 1500)
  }, [])

  const sectionRootFiles = useMemo(
    () => rootFilesForSection(browseSection),
    [browseSection],
  )

  const revealIds = useCallback((ids: string[]) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      for (const id of ids) next.add(id)
      return next
    })
  }, [])

  const restoreFrame = useCallback((frame: NcFrame) => {
    const file = frame.fileId
      ? findFileById(rootFilesForSection(frame.section), frame.fileId)
      : null
    const expand = frame.view === 'folder' ? frame.path.slice(0, -1) : frame.path
    setBrowseSection(frame.section)
    setCurrentFolderPath(frame.path)
    setMainView(frame.view)
    setSelectedId(file?.id ?? null)
    setSelectedFile(file)
    setPreviewFile(file)
    revealIds([frame.section, ...expand])
  }, [revealIds])

  const goBack = useCallback(() => {
    const prev = backRef.current
    if (!prev.length) return
    const frame = prev[prev.length - 1]
    backRef.current = prev.slice(0, -1)
    const current = frameRef.current
    forwardRef.current = [{ ...current, path: [...current.path] }, ...forwardRef.current]
    restoreFrame(frame)
    setHistoryTick((tick) => tick + 1)
  }, [restoreFrame])

  const goForward = useCallback(() => {
    const next = forwardRef.current
    if (!next.length) return
    const frame = next[0]
    forwardRef.current = next.slice(1)
    const current = frameRef.current
    backRef.current = [...backRef.current, { ...current, path: [...current.path] }]
    restoreFrame(frame)
    setHistoryTick((tick) => tick + 1)
  }, [restoreFrame])

  const openSection = useCallback((section: BrowseSection) => {
    pushHistory()
    const path = section === 'allFiles' ? defaultRootPath(defaultRoot) : []
    const nodes = rootFilesForSection(section)
    const file =
      section === 'allFiles'
        ? INITIAL_FILE
        : nodes.find((item) => item.type !== 'folder') ?? nodes[0] ?? null
    setBrowseSection(section)
    setCurrentFolderPath(path)
    revealIds([section, ...path])
    setSelectedId(file?.id ?? null)
    setSelectedFile(file)
    setPreviewFile(file)
    setMainView('folder')
  }, [defaultRoot, revealIds, pushHistory])

  const openFolder = useCallback((section: BrowseSection, folderId: string) => {
    pushHistory()
    const path = folderTrail(rootFilesForSection(section), folderId) ?? [folderId]
    setBrowseSection(section)
    setCurrentFolderPath(path)
    revealIds([section, ...path.slice(0, -1)])
    setSelectedId(null)
    setSelectedFile(null)
    setPreviewFile(null)
    setMainView('folder')
  }, [revealIds, pushHistory])

  const selectFromTree = useCallback((section: BrowseSection, file: MockNextcloudFile) => {
    pushHistory()
    const path = ancestorFolderPath(rootFilesForSection(section), file.id) ?? []
    setBrowseSection(section)
    setCurrentFolderPath(path)
    revealIds([section, ...path])
    setSelectedId(file.id)
    setSelectedFile(file)
    setMainView('file')
  }, [revealIds, pushHistory])

  // Navigate into current folder path to get current folder items
  const currentFolderItems = useMemo(() => {
    let items = sectionRootFiles
    for (const folderId of currentFolderPath) {
      const folder = items.find(item => item.id === folderId && item.type === 'folder')
      if (folder && folder.children) {
        items = folder.children
      } else {
        return []
      }
    }
    return items
  }, [sectionRootFiles, currentFolderPath])

  const listedItems = useMemo(() => {
    let next = currentFolderItems
    if (kindFilter !== 'all') {
      next = next.filter((item) => (kindFilter === 'folder' ? item.type === 'folder' : item.type !== 'folder'))
    }
    if (viewMode === 'list') return next
    return [...next].sort((a, b) => a.name.localeCompare(b.name) * (sortDir === 'asc' ? 1 : -1))
  }, [currentFolderItems, kindFilter, sortDir, viewMode])

  const navigateToFolder = useCallback((folderId: string) => {
    const folder = currentFolderItems.find(item => item.id === folderId && item.type === 'folder')
    if (folder) {
      pushHistory()
      setCurrentFolderPath(prev => [...prev, folderId])
      setSelectedId(null)
      setSelectedFile(null)
      setPreviewFile(null)
      setMainView('folder')
    }
  }, [currentFolderItems, pushHistory])

  const handleSelect = useCallback((file: MockNextcloudFile) => {
    setSelectedId(file.id)
    setSelectedFile(file)
    setPreviewFile(file)
    if (!previewOpen) setPreviewOpen(true)
  }, [previewOpen])

  const handleDoubleClick = useCallback((file: MockNextcloudFile) => {
    if (file.type === 'folder' && !file.isEncrypted) {
      navigateToFolder(file.id)
    } else {
      pushHistory()
      setSelectedId(file.id)
      setSelectedFile(file)
      setMainView('file')
    }
  }, [navigateToFolder, pushHistory])

  const handleOpen = useCallback(() => {
    if (!selectedFile) return
    if (selectedFile.type === 'folder' && !selectedFile.isEncrypted) {
      navigateToFolder(selectedFile.id)
      setSelectedId(null)
      setSelectedFile(null)
      setPreviewFile(null)
    } else {
      pushHistory()
      setMainView('file')
    }
  }, [selectedFile, navigateToFolder, pushHistory])

  const navigateUp = useCallback(() => {
    if (mainView === 'file') {
      pushHistory()
      setMainView('folder')
      setSelectedId(null)
      setSelectedFile(null)
      setPreviewFile(null)
      return
    }
    if (currentFolderPath.length > 0) {
      pushHistory()
      setCurrentFolderPath(prev => prev.slice(0, -1))
      setSelectedId(null)
      setSelectedFile(null)
      setPreviewFile(null)
    }
  }, [currentFolderPath, mainView, pushHistory])

  const navigateToPathIndex = useCallback((index: number) => {
    pushHistory()
    setCurrentFolderPath(prev => prev.slice(0, index + 1))
    setSelectedId(null)
    setSelectedFile(null)
    setPreviewFile(null)
    setMainView('folder')
  }, [pushHistory])

  const breadcrumbSegments: BreadcrumbSegment[] = useMemo(() => {
    const segments: BreadcrumbSegment[] = [
      { label: getSectionLabel(browseSection), path: null, onClick: () => {
        pushHistory()
        setCurrentFolderPath([])
        setSelectedId(null)
        setSelectedFile(null)
        setPreviewFile(null)
        setMainView('folder')
      }}
    ]
    
    // Add folder path crumbs
    let items = sectionRootFiles
    currentFolderPath.forEach((folderId, index) => {
      const folder = items.find(item => item.id === folderId)
      if (folder) {
        segments.push({
          label: folder.name,
          path: null,
          onClick: () => navigateToPathIndex(index)
        })
        if (folder.children) {
          items = folder.children
        }
      }
    })
    
    if (mainView === 'file' && selectedFile && selectedFile.type !== 'folder') {
      segments.push({ label: selectedFile.name, path: null })
    }

    return segments
  }, [browseSection, currentFolderPath, sectionRootFiles, navigateToPathIndex, mainView, selectedFile, pushHistory])

  const contextMenuEntries = useMemo((): EntityContextMenuEntry[] => {
    if (!ctxMenu) return []
    const { file } = ctxMenu
    const isImportable = isNextcloudFileImportable(file.type)
    const ExternalIcon = resolveOpenExternalIcon()

    return [
      {
        label: 'Preview',
        icon: OpenDestinationIcons.preview,
        onSelect: () => {
          setPreviewFile(file)
          setPreviewOpen(true)
          setSelectedId(file.id)
          setSelectedFile(file)
          setMainView('folder')
          setCtxMenu(null)
        },
        disabled: file.type === 'folder' && !file.isEncrypted,
      },
      {
        label: 'Open',
        icon: OpenDestinationIcons.open,
        onSelect: () => {
          setCtxMenu(null)
          handleOpen()
        },
      },
      {
        label: 'Import',
        icon: <Import className="h-4 w-4" />,
        onSelect: () => handleImport(file),
        disabled: !isImportable || importing === file.id || file.isEncrypted,
      },
      {
        label: 'Link without copy',
        icon: <Link2 className="h-4 w-4" />,
        onSelect: () => setCtxMenu(null),
        disabled: file.type === 'folder' || file.isEncrypted,
      },
      { separator: true },
      {
        label: 'Open in Nextcloud',
        icon: <ExternalIcon className="h-4 w-4" />,
        onSelect: () => setCtxMenu(null),
      },
      {
        label: 'Download',
        icon: <Download className="h-4 w-4" />,
        onSelect: () => setCtxMenu(null),
        disabled: file.type === 'folder' || file.isEncrypted,
      },
    ]
  }, [ctxMenu, importing, handleImport, handleOpen])

  return (
    <div
      className="h-screen w-screen overflow-hidden bg-background text-foreground antialiased"
      style={maaterProtoAppSkinStyle()}
      data-testid={testIds.nextcloud.harness}
    >
      <ProtoAppHeader
        appTheme={theme}
        onAppTheme={setTheme}
        themeControl="light-dark"
        badge="proto"
      />

        <div className="flex h-[calc(100vh-3.5rem)]">
        <ProtoIconRail active="documents" />

        <div className="flex flex-1 border-l border-border">
          <ProtoModePanel
            title="Nextcloud"
            footer={
              <div className="flex items-center gap-2 px-1">
                {authState === 'connected' ? (
                  <>
                    <Badge className="border-green-500/20 bg-green-500/10 text-[10px] text-green-600">
                      Connected
                    </Badge>
                    <button
                      type="button"
                      onClick={handleDisconnect}
                      className="text-[10px] text-muted-foreground hover:text-destructive"
                    >
                      Disconnect
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfigureOpen(true)}
                      className="ml-auto text-[10px] text-muted-foreground hover:text-foreground"
                    >
                      Configure
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => setConfigureOpen(true)}
                    className="rounded bg-primary px-2 py-0.5 text-[10px] font-medium text-primary-foreground"
                  >
                    Configure
                  </button>
                )}
              </div>
            }
          >
            {authState === 'connected' && (
              <nav
                className="ws-nav-section -mx-2"
                style={wsNavSectionStyle()}
                aria-label="Nextcloud files"
              >
                {BROWSE_SURFACES.map((surface) => {
                  const nodes = rootFilesForSection(surface.id)
                  const expanded = expandedIds.has(surface.id)
                  const sectionOn = browseSection === surface.id
                  const atRoot = sectionOn && currentFolderPath.length === 0 && !selectedId
                  return (
                    <div key={surface.id}>
                      <SidebarTreeRow
                        padLeft={wsNavRowPadLeftPx(0)}
                        label={surface.label}
                        icon={sidebarFolderIcon(expanded)}
                        active={atRoot}
                        contains={sectionOn && !atRoot}
                        className="cursor-pointer"
                        chevron={{
                          expanded,
                          onClick: (e) => {
                            e.stopPropagation()
                            toggleExpanded(surface.id)
                          },
                        }}
                        onClick={() => openSection(surface.id)}
                      />
                      {expanded && (
                          <NextcloudBrowseTree
                          items={nodes}
                          section={surface.id}
                          activeSection={browseSection}
                          currentPath={currentFolderPath}
                          expandedIds={expandedIds}
                          onOpenFolder={(id) => openFolder(surface.id, id)}
                          onSelectFile={(file) => selectFromTree(surface.id, file)}
                          onToggle={toggleExpanded}
                          onContextMenu={handleContextMenu}
                          openFileId={mainView === 'file' ? selectedId : null}
                        />
                      )}
                    </div>
                  )
                })}
              </nav>
            )}
          </ProtoModePanel>

          <main className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
              <div className="flex h-9 shrink-0 items-center gap-2 border-b border-border px-3">
                <span className="text-[10px] font-medium text-muted-foreground">Frame</span>
                <MaaterProtoSegmentTrack
                  value={errorState ?? 'live'}
                  onChange={(id) => setErrorState(id === 'live' ? null : (id as '404' | '403'))}
                  options={[
                    { id: 'live', label: 'Live' },
                    { id: '404', label: 'Not found' },
                    { id: '403', label: 'Denied' },
                  ]}
                />
              </div>

              <ProtoTabRow 
                tabs={[{ id: 't1', label: 'Nextcloud' }]} 
                activeId="t1"
                onSelect={() => {}}
              />

              <IndexSurfaceShell
                className="min-h-0 flex-1"
                header={
                  <FolderTitleRow
                    canGoBack={historyTick >= 0 && backRef.current.length > 0}
                    canGoForward={forwardRef.current.length > 0}
                    onGoBack={goBack}
                    onGoForward={goForward}
                    segments={breadcrumbSegments.slice(0, -1)}
                    title={breadcrumbSegments[breadcrumbSegments.length - 1]?.label ?? 'Files'}
                    icon={
                      mainView === 'file' && selectedFile ? (
                        (() => {
                          const Icon = getFileIcon(selectedFile.type)
                          return <Icon className="h-4 w-4 shrink-0" />
                        })()
                      ) : (
                        <Folder className="h-4 w-4 shrink-0 text-blue-500" aria-hidden />
                      )
                    }
                    onReload={() => setToast('Reloaded folder')}
                    previewOpen={previewOpen}
                    onTogglePreview={handlePreviewToggle}
                    menuItems={authState === 'connected' ? [{
                      label: 'Import',
                      icon: <Import className="h-4 w-4" />,
                      onSelect: () => setShareImportOpen(true),
                    }] : []}
                    testId={testIds.nextcloud.folderToolbar}
                    menuTestId={testIds.nextcloud.menuButton}
                  />
                }
                footer={
                  <ListSurfaceStatusBar
                    trailing={
                      mainView === 'file' && selectedFile
                        ? selectedFile.name
                        : `${currentFolderItems.length} ${currentFolderItems.length === 1 ? 'item' : 'items'}`
                    }
                    tips={LIST_GESTURE_TIPS_DRIVE_WITH_PREVIEW}
                  />
                }
              >
                {mainView === 'file' && selectedFile && selectedFile.type !== 'folder' ? (
                  <div className="min-h-0 flex-1 overflow-auto">
                    <FilePreview
                      file={selectedFile}
                      onImport={handleImport}
                      onSelect={handleSelect}
                      onNavigate={navigateToFolder}
                    />
                  </div>
                ) : (
                <PanelGroup direction="horizontal" className="min-h-0 flex-1">
                <Panel minSize={35} className="flex min-h-0 flex-col overflow-hidden">
                {authState === 'connected' && !errorState ? (
                  <FolderFilterRow
                    viewMode={viewMode}
                    onViewMode={setViewMode}
                    kindFilter={kindFilter}
                    onKindFilter={setKindFilter}
                    sortDir={sortDir}
                    onSortDir={setSortDir}
                    testId={testIds.nextcloud.filterRow}
                    viewModeTestId={testIds.nextcloud.viewMode}
                  />
                ) : null}
                <div className="min-h-0 flex-1 overflow-y-auto">
                    {authState !== 'connected' && (
                      <div className="flex items-center justify-center h-full p-6">
                        <div className="text-center space-y-3 max-w-sm">
                          <Globe className="h-12 w-12 mx-auto text-muted-foreground" />
                          <h3 className="text-sm font-semibold">Connect to browse files</h3>
                          <p className="text-xs text-muted-foreground">
                            Click Configure and connect your Nextcloud instance to browse files.
                          </p>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setConfigureOpen(true)}
                          >
                            Configure
                          </Button>
                        </div>
                      </div>
                    )}

                    {authState === 'connected' && errorState && (
                  <div className="flex items-center justify-center h-full p-6">
                    <div className="text-center space-y-4 max-w-sm">
                      <div className="mx-auto size-16 rounded-full bg-red-500/10 flex items-center justify-center">
                        <X className="h-8 w-8 text-red-500" />
                      </div>
                      <div className="space-y-2">
                        <h3 className="text-lg font-semibold">
                          {errorState === '404' ? 'Not found' : 'Access denied'}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {errorState === '404'
                            ? 'The folder or file you requested does not exist or has been deleted.'
                            : 'You do not have permission to access this resource.'}
                        </p>
                      </div>
                      <div className="flex gap-2 justify-center">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setErrorState(null)}
                        >
                          Go back
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setConfigureOpen(true)}
                        >
                          Configure
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                    {authState === 'connected' && !errorState && (
                      <>
                        {currentFolderPath.length > 0 && viewMode === 'list' ? (
                          <UpOneLevelRow density="list" onClick={navigateUp} />
                        ) : null}
                        {listedItems.length === 0 ? (
                          <p className="px-6 py-8 text-center text-xs text-muted-foreground">
                            {browseSection === 'sharedIn' && 'No files shared with you yet'}
                            {browseSection === 'sharesOut' && 'No shares created yet'}
                            {browseSection === 'allFiles' && 'This folder is empty'}
                          </p>
                        ) : viewMode === 'list' ? (
                          <ul>
                            {listedItems.map((file) => {
                              const Icon = getFileIcon(file.type)
                              return (
                                <li key={file.id}>
                                  <ExtensionListRow
                                    selected={selectedId === file.id}
                                    testId={testIds.nextcloud.fileRow}
                                    onSelect={() => handleSelect(file)}
                                    onOpen={() => handleDoubleClick(file)}
                                    onContextMenu={(e) => handleContextMenu(e, file)}
                                  >
                                    <span className="mt-0.5 [&_svg]:h-4 [&_svg]:w-4">
                                      <Icon />
                                    </span>
                                    <div className="min-w-0 flex-1">
                                      <div className="truncate text-sm font-medium">{file.name}</div>
                                      <div className="mt-0.5 truncate text-xs text-muted-foreground">
                                        {file.sharedBy ? `Shared by ${file.sharedBy}` : formatDate(file.modified)}
                                      </div>
                                    </div>
                                    {file.isEncrypted && (
                                      <span className="shrink-0 self-center">
                                        <Lock className="size-3 text-muted-foreground" />
                                      </span>
                                    )}
                                  </ExtensionListRow>
                                </li>
                              )
                            })}
                          </ul>
                        ) : (
                          <div>
                            {currentFolderPath.length > 0 ? (
                              <UpOneLevelRow onClick={navigateUp} />
                            ) : null}
                          <div className={viewMode === 'grid' ? `${FOLDER_CARD_GRID_CLASS} p-4` : 'space-y-3 p-4'}>
                            {listedItems.map((file) => {
                              const Icon = getFileIcon(file.type)
                              const markdown = MOCK_FILE_CONTENT[file.name]
                              return (
                                <FolderItemCard
                                  key={file.id}
                                  selected={selectedId === file.id}
                                  stacked={viewMode === 'card'}
                                  title={file.name}
                                  subtitle={file.sharedBy ? `Shared by ${file.sharedBy}` : formatDate(file.modified)}
                                  icon={<Icon className="h-4 w-4" />}
                                  preview={markdown ? (
                                    <DocumentTileMarkdown source={markdown} title={file.name.replace(/\.[^.]+$/, '')} />
                                  ) : (
                                    file.type === 'folder' ? 'Folder' : file.type
                                  )}
                                  testId={testIds.nextcloud.fileRow}
                                  onSelect={() => handleSelect(file)}
                                  onOpen={() => handleDoubleClick(file)}
                                  onContextMenu={(event) => handleContextMenu(event, file)}
                                />
                              )
                            })}
                          </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </Panel>

                {previewOpen && (
                  <>
                    <SecondaryPreviewResizeHandle />
                    <SecondaryPanel
                      panelType="nextcloud-preview"
                      panelId="nextcloud-preview"
                      defaultSize={PANEL_SIZES.SECONDARY_DEFAULT}
                      minSize={PANEL_SIZES.SECONDARY_MIN}
                      background="card"
                      className="min-w-0 overflow-hidden"
                    >
                      {previewFile ? (
                        <>
                          <PreviewPaneHeader
                            row
                            icon={(() => {
                              const Icon = getFileIcon(previewFile.type)
                              return <Icon className="size-4" />
                            })()}
                            label={previewFile.type === 'folder' ? 'Folder' : 'File preview'}
                            onOpen={handleOpen}
                            onClose={() => setPreviewOpen(false)}
                          />
                          <div className="min-h-0 flex-1 overflow-auto">
                            <FilePreview
                              file={previewFile}
                              onImport={handleImport}
                              onSelect={handleSelect}
                              onNavigate={navigateToFolder}
                            />
                          </div>
                        </>
                      ) : (
                        <IndexPreviewEmpty
                          row
                          noun="file"
                          label="File preview"
                          onClose={() => setPreviewOpen(false)}
                        />
                      )}
                    </SecondaryPanel>
                  </>
                )}
                </PanelGroup>
                )}
              </IndexSurfaceShell>
          </main>
        </div>
      </div>

      {ctxMenu && (
        <EntityContextMenu
          x={ctxMenu.x}
          y={ctxMenu.y}
          entries={contextMenuEntries}
          onClose={() => setCtxMenu(null)}
        />
      )}

      {configureOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-background rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-auto">
            <ConfigurePanel
              authState={authState}
              connecting={connecting}
              instanceUrl={instanceUrl}
              username={username}
              defaultRoot={defaultRoot}
              importDestination={importDestination}
              testingConnection={testingConnection}
              connectionTestResult={connectionTestResult}
              onInstanceUrlChange={setInstanceUrl}
              onUsernameChange={setUsername}
              onDefaultRootChange={setDefaultRoot}
              onImportDestinationChange={setImportDestination}
              onTestConnection={handleTestConnection}
              onConnect={handleConnect}
              onDisconnect={handleDisconnect}
              onClose={() => setConfigureOpen(false)}
            />
          </div>
        </div>
      )}

      {toast && (
        <div className="fixed bottom-6 left-1/2 z-50 flex -translate-x-1/2 items-center gap-3 rounded-lg bg-foreground px-4 py-2.5 text-sm font-medium text-background shadow-xl">
          <Check className="h-4 w-4 text-green-400" />
          {toast}
          <button
            type="button"
            onClick={() => setToast(null)}
            className="ml-1 opacity-60 hover:opacity-100"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {shareImportOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
          <div
            className="flex w-[440px] flex-col rounded-xl border shadow-2xl"
            style={{ borderColor: 'var(--line)', background: 'var(--paper-raised)' }}
          >
            <div
              className="flex items-center justify-between border-b px-5 py-4"
              style={{ borderColor: 'var(--line)' }}
            >
              <div className="space-y-0.5">
                <h2 className="text-sm font-semibold" style={{ color: 'var(--ink)' }}>
                  Import from Nextcloud
                </h2>
                <p className="text-xs" style={{ color: 'var(--ink-soft)' }}>
                  Paste a share link. Files you already see use Import on the row menu.
                </p>
              </div>
              <button type="button" onClick={() => setShareImportOpen(false)} style={{ color: 'var(--ink-soft)' }}>
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-4 px-5 py-4">
              <div className="space-y-2">
                <Label htmlFor="share-link-paste">Paste share link</Label>
                <Input
                  id="share-link-paste"
                  type="text"
                  placeholder="https://cloud.example.org/s/abc123token"
                  value={shareLinkToken}
                  onChange={(e) => setShareLinkToken(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="share-import-destination">Destination folder</Label>
                <Input
                  id="share-import-destination"
                  type="text"
                  value={importDestination}
                  onChange={(e) => setImportDestination(e.target.value)}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={() => setShareImportOpen(false)}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  disabled={!shareLinkToken.trim()}
                  onClick={() => {
                    setToast(`Imported share link to ${importDestination}`)
                    setTimeout(() => setToast(null), 3000)
                    setShareImportOpen(false)
                    setShareLinkToken('')
                  }}
                >
                  Import
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {importModalOpen && importingFile && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-background w-full min-w-0 max-w-md mx-4 overflow-hidden rounded-lg p-6 shadow-xl space-y-4">
            <div className="space-y-2">
              <h2 className="text-lg font-semibold">Import to Maater</h2>
              <p className="text-sm text-muted-foreground">
                Import <span className="font-medium">{importingFile.name}</span> from Nextcloud
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="import-destination">Destination folder</Label>
              <Input
                id="import-destination"
                type="text"
                value={importDestination}
                onChange={(e) => setImportDestination(e.target.value)}
                placeholder="workspace/inbox/nextcloud"
              />
            </div>

            <div className="min-w-0 overflow-hidden rounded-lg border border-border bg-muted/20 p-4 space-y-2">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase">
                Frontmatter linkage
              </h3>
              <pre className="whitespace-pre-wrap break-all text-xs font-mono">
{`---
source: nextcloud
source_url: ${instanceUrl}/f/${importingFile.id}
imported_at: ${new Date().toISOString()}
imported_by: ${username}
---`}
              </pre>
            </div>

            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setImportModalOpen(false)
                  setImportingFile(null)
                }}
              >
                Cancel
              </Button>
              <Button onClick={confirmImport}>
                Import
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// --- Sub-components -------------------------------------------------------

function ConfigurePanel({
  authState,
  connecting,
  instanceUrl,
  username,
  defaultRoot,
  importDestination,
  testingConnection,
  connectionTestResult,
  onInstanceUrlChange,
  onUsernameChange,
  onDefaultRootChange,
  onImportDestinationChange,
  onTestConnection,
  onConnect,
  onDisconnect,
  onClose,
}: {
  authState: AuthState
  connecting: boolean
  instanceUrl: string
  username: string
  defaultRoot: string
  importDestination: string
  testingConnection: boolean
  connectionTestResult: 'success' | 'error' | null
  onInstanceUrlChange: (value: string) => void
  onUsernameChange: (value: string) => void
  onDefaultRootChange: (value: string) => void
  onImportDestinationChange: (value: string) => void
  onTestConnection: () => void
  onConnect: () => void
  onDisconnect: () => void
  onClose: () => void
}) {
  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Configure Nextcloud</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Connect to your Nextcloud instance to browse and import files.
          </p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
        >
          <X className="size-4" />
        </Button>
      </div>
      {authState === 'disconnected' && (
        <>
          <div className="rounded-lg border border-border bg-muted/20 p-5 space-y-4">
            <h3 className="text-sm font-semibold">Connection</h3>
            <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="nc-instance-url">Instance URL</Label>
                  <Input
                    id="nc-instance-url"
                    type="url"
                    placeholder="https://cloud.example.org"
                    value={instanceUrl}
                    onChange={(e) => onInstanceUrlChange(e.target.value)}
                    data-testid={testIds.nextcloud.instanceUrlInput}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="nc-username">Username</Label>
                  <Input
                    id="nc-username"
                    type="text"
                    placeholder="alice"
                    value={username}
                    onChange={(e) => onUsernameChange(e.target.value)}
                    data-testid={testIds.nextcloud.usernameInput}
                  />
                  <p className="text-xs text-muted-foreground">
                    Uses Login Flow v2 - no password needed
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="nc-import-destination">Import into</Label>
                  <Input
                    id="nc-import-destination"
                    type="text"
                    placeholder="workspace/inbox/nextcloud"
                    value={importDestination}
                    onChange={(e) => onImportDestinationChange(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Default destination folder for imported files
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={onTestConnection}
                  disabled={testingConnection || !instanceUrl}
                  className="flex-1"
                >
                  {testingConnection ? (
                    <>
                      <Loader2 className="mr-2 size-4 animate-spin" />
                      Testing...
                    </>
                  ) : (
                    'Test connection'
                  )}
                </Button>
                {connectionTestResult === 'success' && (
                  <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                    <Check className="size-4" />
                    <span>Connected</span>
                  </div>
                )}
              </div>
            </div>

            <div className="rounded-lg border border-border bg-muted/20 p-5 space-y-4">
              <h3 className="text-sm font-semibold">Get started</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Import files from Nextcloud into <code className="px-1.5 py-0.5 rounded bg-muted text-xs">/nextcloud</code>. Work stays in Nextcloud. Context compounds in Maater.
              </p>
              <ol className="space-y-2 text-sm">
                <li className="flex gap-3">
                  <span className="shrink-0 text-muted-foreground">1.</span>
                  <span>Connect your Nextcloud instance above</span>
                </li>
                <li className="flex gap-3">
                  <span className="shrink-0 text-muted-foreground">2.</span>
                  <span>Browse files from the sidebar</span>
                </li>
                <li className="flex gap-3">
                  <span className="shrink-0 text-muted-foreground">3.</span>
                  <span>Import markdown/text files into Maater</span>
                </li>
              </ol>
              <Button
                onClick={onConnect}
                disabled={connecting || !instanceUrl || !username}
                className="w-full"
                data-testid={testIds.nextcloud.connectButton}
              >
                {connecting ? (
                  <>
                    <Loader2 className="mr-2 size-4 animate-spin" />
                    Connecting...
                  </>
                ) : (
                  'Connect to Nextcloud'
                )}
              </Button>
          </div>
        </>
      )}

      {authState === 'connected' && (
        <>
          <div className="rounded-lg border border-green-500/30 bg-green-500/10 p-5 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold">Connected</h3>
                  <p className="text-xs text-muted-foreground mt-1">
                    {username}@{new URL(instanceUrl).hostname}
                  </p>
                </div>
                <Badge variant="outline" className="border-green-500/30 bg-green-500/10 text-green-700 dark:text-green-400">
                  <Check className="mr-1 size-3" />
                  Active
                </Badge>
              </div>
              <Button
                variant="outline"
                onClick={onDisconnect}
                className="w-full"
                data-testid={testIds.nextcloud.disconnectButton}
              >
                Disconnect
              </Button>
            </div>

            <div className="rounded-lg border border-border bg-muted/20 p-5 space-y-4">
              <h3 className="text-sm font-semibold">Settings</h3>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="nc-default-root">Default browse root</Label>
                  <Input
                    id="nc-default-root"
                    type="text"
                    placeholder="/Maater"
                    value={defaultRoot}
                    onChange={(e) => onDefaultRootChange(e.target.value)}
                    data-testid={testIds.nextcloud.defaultRootInput}
                  />
                  <p className="text-xs text-muted-foreground">
                    Starting folder for browsing (leave empty for home directory)
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="nc-import-destination-connected">Import into</Label>
                  <Input
                    id="nc-import-destination-connected"
                    type="text"
                    placeholder="workspace/inbox/nextcloud"
                    value={importDestination}
                    onChange={(e) => onImportDestinationChange(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Default destination folder for imported files
                  </p>
                </div>
              </div>
          </div>
        </>
      )}
    </div>
  )
}

function NextcloudBrowseTree({
  items,
  section,
  activeSection,
  currentPath,
  expandedIds,
  onOpenFolder,
  onSelectFile,
  onToggle,
  onContextMenu,
  openFileId,
  level = 0,
}: {
  items: MockNextcloudFile[]
  section: BrowseSection
  activeSection: BrowseSection
  currentPath: string[]
  expandedIds: Set<string>
  onOpenFolder: (id: string) => void
  onSelectFile: (file: MockNextcloudFile) => void
  onToggle: (id: string) => void
  onContextMenu: (event: React.MouseEvent, file: MockNextcloudFile) => void
  openFileId: string | null
  level?: number
}) {
  const sectionOn = activeSection === section
  return items.map((item) => {
    const padLeft = wsNavRowPadLeftPx(level + 1)
    if (item.type === 'folder') {
      const hasChildren = !!item.children?.length
      const expanded = expandedIds.has(item.id)
      const inPath = sectionOn && currentPath.includes(item.id)
      const focused =
        inPath && currentPath[currentPath.length - 1] === item.id && !openFileId
      return (
        <div key={item.id}>
          <SidebarTreeRow
            padLeft={padLeft}
            label={item.name}
            icon={sidebarFolderIcon(expanded)}
            subtitle={item.isEncrypted ? 'Locked' : undefined}
            active={focused}
            contains={inPath && !focused}
            className="cursor-pointer"
            chevron={
              hasChildren
                ? {
                    expanded,
                    onClick: (e) => {
                      e.stopPropagation()
                      onToggle(item.id)
                    },
                  }
                : undefined
            }
            onClick={() => onOpenFolder(item.id)}
            onContextMenu={(event) => onContextMenu(event, item)}
          />
          {expanded && hasChildren && (
            <NextcloudBrowseTree
              items={item.children!}
              section={section}
              activeSection={activeSection}
              currentPath={currentPath}
              expandedIds={expandedIds}
              onOpenFolder={onOpenFolder}
              onSelectFile={onSelectFile}
              onToggle={onToggle}
              onContextMenu={onContextMenu}
              openFileId={openFileId}
              level={level + 1}
            />
          )}
        </div>
      )
    }
    return (
      <SidebarTreeRow
        key={item.id}
        padLeft={padLeft}
        label={item.name}
        icon={sidebarFileIcon(item.name)}
        active={sectionOn && openFileId === item.id}
        className="cursor-pointer"
        onClick={() => onSelectFile(item)}
        onContextMenu={(event) => onContextMenu(event, item)}
      />
    )
  })
}

function FileFacts({ file }: { file: MockNextcloudFile }) {
  const rows: Array<[string, string]> = [
    ['Path', file.path],
    ['Modified', formatDate(file.modified)],
  ]
  if (file.size) rows.push(['Size', formatFileSize(file.size)])
  if (file.sharedBy) rows.push(['Shared by', file.sharedBy])
  if (file.fileId) rows.push(['File id', file.fileId])
  return (
    <dl className="grid grid-cols-[88px_1fr] gap-x-3 gap-y-1.5 text-xs">
      {rows.map(([label, value]) => (
        <div key={label} className="contents">
          <dt className="text-muted-foreground">{label}</dt>
          <dd className="min-w-0 truncate">{value}</dd>
        </div>
      ))}
    </dl>
  )
}

function FilePreview({
  file,
  onImport,
  onSelect,
  onNavigate,
}: {
  file: MockNextcloudFile
  onImport: (file: MockNextcloudFile) => void
  onSelect: (file: MockNextcloudFile) => void
  onNavigate: (folderId: string) => void
}) {
  const ExternalIcon = resolveOpenExternalIcon()

  if (file.isEncrypted) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 p-6 text-center">
        <Lock className="size-10 text-muted-foreground" />
        <div className="max-w-xs space-y-1">
          <p className="text-sm font-medium">End-to-end encrypted</p>
          <p className="text-xs leading-relaxed text-muted-foreground">
            Maater cannot preview or import this. Open it in Nextcloud.
          </p>
        </div>
        <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs">
          <ExternalIcon className="h-3.5 w-3.5" />
          Open in Nextcloud
        </Button>
      </div>
    )
  }

  if (file.type === 'folder') {
    const children = file.children || []
    if (children.length === 0) {
      return <p className="px-4 py-6 text-xs text-muted-foreground">Empty folder</p>
    }
    return (
      <ul>
        {children.map((child) => {
          const Icon = getFileIcon(child.type)
          return (
            <li key={child.id}>
              <ExtensionListRow
                selected={false}
                onSelect={() => {
                  if (child.type === 'folder') onNavigate(child.id)
                  else onSelect(child)
                }}
                onOpen={() => {
                  if (child.type === 'folder') onNavigate(child.id)
                  else onSelect(child)
                }}
                onContextMenu={(e) => e.preventDefault()}
              >
                <span className="mt-0.5 [&_svg]:h-4 [&_svg]:w-4">
                  <Icon />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">{child.name}</div>
                  <div className="mt-0.5 truncate text-xs text-muted-foreground">
                    {formatDate(child.modified)}
                  </div>
                </div>
                {child.isEncrypted && (
                  <span className="shrink-0 self-center">
                    <Lock className="size-3 text-muted-foreground" />
                  </span>
                )}
              </ExtensionListRow>
            </li>
          )
        })}
      </ul>
    )
  }

  const importable = isNextcloudFileImportable(file.type)
  const content = MOCK_FILE_CONTENT[file.name]
  const binaryNote =
    file.type === 'pdf'
      ? 'PDF stays in Nextcloud. Link it here, or open it there. Text import is for markdown and plain text.'
      : file.type === 'office'
        ? 'Office files stay in Nextcloud. Open them there. Text import is for markdown and plain text.'
        : file.type === 'image'
          ? 'Image stays in Nextcloud. Open it there to view the original.'
          : null

  return (
    <div className="space-y-4 p-4">
      <div className="space-y-1">
        <h3 className="text-sm font-semibold">{file.name}</h3>
        <p className="text-xs text-muted-foreground">
          {file.sharedBy ? `Shared by ${file.sharedBy}` : formatDate(file.modified)}
        </p>
      </div>
      <FileFacts file={file} />
      {content && file.type === 'markdown' ? (
        <div className="rounded-lg border border-border bg-muted/20 p-4">
          <DocumentTileMarkdown source={content} title={file.name.replace(/\.[^.]+$/, '')} />
        </div>
      ) : content ? (
        <div className="whitespace-pre-wrap rounded-lg border border-border bg-muted/20 p-4 text-[13px] leading-relaxed">
          {content}
        </div>
      ) : binaryNote ? (
        <div className="rounded-lg border border-border bg-muted/20 px-4 py-3 text-xs leading-relaxed text-muted-foreground">
          {binaryNote}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">No preview for this file.</p>
      )}
      <div className="flex flex-wrap gap-2">
        <Button size="sm" className="h-8 gap-1.5 text-xs">
          <ExternalIcon className="h-3.5 w-3.5" />
          Open in Nextcloud
        </Button>
        {importable ? (
          <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs" onClick={() => onImport(file)}>
            <Import className="h-3.5 w-3.5" />
            Import to Maater
          </Button>
        ) : (
          <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs">
            <Link2 className="h-3.5 w-3.5" />
            Link without copy
          </Button>
        )}
      </div>
    </div>
  )
}

// --- Helpers --------------------------------------------------------------

function sidebarFolderIcon(expanded: boolean) {
  const Icon = expanded ? FolderOpen : Folder
  return <Icon className="h-3.5 w-3.5" style={{ color: 'hsl(var(--primary) / 0.8)' }} />
}

function sidebarFileIcon(name: string) {
  return getDocumentDisplayIcon({ filePath: name }, { className: 'h-3.5 w-3.5 shrink-0' })
}

function getFileIcon(type: NextcloudFileKind) {
  switch (type) {
    case 'folder':
      return Folder
    case 'markdown':
      return FileText
    case 'text':
      return FileCode
    case 'pdf':
      return File
    case 'image':
      return ImageIcon
    case 'office':
      return File
    default:
      return File
  }
}

function rootFilesForSection(section: BrowseSection): MockNextcloudFile[] {
  if (section === 'sharedIn') return MOCK_NEXTCLOUD_SHARED
  if (section === 'sharesOut') return MOCK_NEXTCLOUD_SHARES_OUT
  return MOCK_NEXTCLOUD_ROOT
}

function defaultRootPath(defaultRoot: string): string[] {
  if (!defaultRoot || defaultRoot === '/') return []
  const name = defaultRoot.toLowerCase().replace(/^\//, '')
  return folderTrailByName(MOCK_NEXTCLOUD_ROOT, name) ?? []
}

function folderTrailByName(
  items: MockNextcloudFile[],
  name: string,
  trail: string[] = [],
): string[] | null {
  for (const item of items) {
    const next = item.type === 'folder' ? [...trail, item.id] : trail
    if (item.type === 'folder' && item.name.toLowerCase() === name) return next
    if (item.children) {
      const found = folderTrailByName(item.children, name, next)
      if (found) return found
    }
  }
  return null
}

function folderTrail(
  items: MockNextcloudFile[],
  id: string,
  trail: string[] = [],
): string[] | null {
  for (const item of items) {
    if (item.id === id && item.type === 'folder') return [...trail, item.id]
    if (item.children) {
      const next = item.type === 'folder' ? [...trail, item.id] : trail
      const found = folderTrail(item.children, id, next)
      if (found) return found
    }
  }
  return null
}

function ancestorFolderPath(
  items: MockNextcloudFile[],
  id: string,
  trail: string[] = [],
): string[] | null {
  for (const item of items) {
    if (item.id === id) return trail
    if (item.children) {
      const next = item.type === 'folder' ? [...trail, item.id] : trail
      const found = ancestorFolderPath(item.children, id, next)
      if (found) return found
    }
  }
  return null
}

function getSectionLabel(section: BrowseSection): string {
  switch (section) {
    case 'sharedIn':
      return 'Shared with me'
    case 'sharesOut':
      return 'Shares you created'
    default:
      return 'All files'
  }
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function formatDate(iso: string): string {
  const date = new Date(iso)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString()
}

function findFileById(files: MockNextcloudFile[], id: string): MockNextcloudFile | null {
  for (const file of files) {
    if (file.id === id) return file
    if (file.children) {
      const found = findFileById(file.children, id)
      if (found) return found
    }
  }
  return null
}
